import { useState } from "react";

import { useI18n } from "../i18n";

import { desktopFeatureForTool } from "../desktopFeatures";

import DropdownMenu from "./dropdownMenu/DropdownMenu";
import { DotsIcon, mermaidLogoIcon, pencilIcon } from "./icons";
import { TOOLS, getToolShortcut, isToolButtonDisabled } from "./Tools";
import { workspaceText } from "./workspaceText";

import type { ToolbarToolType } from "./Tools";
import type {
  AppClassProperties,
  AppProps,
  AppState,
  UIAppState,
} from "../types";

export const Toolbar = ({
  app,
  appState,
  setAppState,
  UIOptions,
  onPenModeToggle,
  onLockToggle,
}: {
  app: AppClassProperties;
  appState: UIAppState;
  setAppState: React.Component<any, AppState>["setState"];
  UIOptions: AppProps["UIOptions"];
  onPenModeToggle: AppClassProperties["togglePenMode"];
  onLockToggle: () => void;
  heading: React.ReactNode;
}) => {
  const { t, langCode } = useI18n();
  const [moreOpen, setMoreOpen] = useState(false);
  const toolButton = (type: ToolbarToolType) => (
    <button
      type="button"
      key={type}
      className="studio-tool"
      title={`${t(`toolBar.${type}`)} · ${getToolShortcut(type)}`}
      aria-label={t(`toolBar.${type}`)}
      aria-pressed={appState.activeTool.type === type}
      data-testid={`toolbar-${type}`}
      disabled={isToolButtonDisabled(app, type)}
      onPointerDown={(event) => {
        if (app.props.desktopPurchaseUrl && desktopFeatureForTool(type)) {
          return;
        }
        app.toolDrag.handleButtonPointerDown(type, event.nativeEvent);
        if (event.pointerType === "pen" && !app.state.penDetected) {
          app.togglePenMode(true);
        }
      }}
      onClick={() => {
        if (app.state.activeTool.type !== type) {
          app.setActiveTool({ type });
        }
      }}
    >
      {TOOLS[type].icon}
    </button>
  );
  const extras: ToolbarToolType[] = [
    "frame",
    "autoshape",
    "embeddable",
    "lasso",
    "bucketfill",
    "laser",
  ];
  if (UIOptions.tools?.image !== false) {
    extras.unshift("image");
  }
  return (
    <nav
      className="studio-tool-dock"
      aria-label={workspaceText(langCode, "tools")}
      data-viewport-ui="side"
    >
      <div className="studio-tool-group">
        {toolButton("selection")}
        {toolButton("hand")}
      </div>
      <div className="studio-tool-group">
        {(
          [
            "rectangle",
            "ellipse",
            "diamond",
            "arrow",
            "line",
            "freedraw",
            "text",
            "stickynote",
          ] as ToolbarToolType[]
        ).map(toolButton)}
      </div>
      <div className="studio-tool-group">
        {toolButton("eraser")}
        <DropdownMenu open={moreOpen}>
          <DropdownMenu.Trigger
            className="studio-tool"
            title={workspaceText(langCode, "more")}
            data-testid="toolbar-more"
            onToggle={() => {
              setMoreOpen(!moreOpen);
              setAppState({ openMenu: null, openPopup: null });
            }}
          >
            {DotsIcon}
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            className="studio-extra-tools"
            align="start"
            onClickOutside={() => setMoreOpen(false)}
            onSelect={() => setMoreOpen(false)}
          >
            {extras.map((type) => (
              <DropdownMenu.Item
                key={type}
                icon={<>{TOOLS[type].icon}</>}
                data-testid={`toolbar-${type}`}
                className={
                  app.props.desktopPurchaseUrl && desktopFeatureForTool(type)
                    ? "desktop-tool-entry"
                    : undefined
                }
                shortcut={
                  TOOLS[type].letterKey || TOOLS[type].numericKey
                    ? getToolShortcut(type)
                    : undefined
                }
                selected={appState.activeTool.type === type}
                disabled={isToolButtonDisabled(app, type)}
                onSelect={() => app.setActiveTool({ type })}
              >
                {t(`toolBar.${type}`)}
                {app.props.desktopPurchaseUrl &&
                  desktopFeatureForTool(type) && (
                    <span className="desktop-feature-badge">EXE</span>
                  )}
              </DropdownMenu.Item>
            ))}
            <DropdownMenu.Separator />
            <div className="studio-menu-heading">
              {workspaceText(langCode, "create")}
            </div>
            <DropdownMenu.Item
              icon={mermaidLogoIcon}
              data-testid="toolbar-mermaid"
              onSelect={() =>
                app.setOpenDialog({ name: "ttd", tab: "mermaid" })
              }
            >
              {t("toolBar.mermaidToExcalidraw")}
              {app.props.desktopPurchaseUrl && (
                <span className="desktop-feature-badge">EXE</span>
              )}
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu>
      </div>
      <div className="studio-tool-group studio-tool-options">
        <button
          type="button"
          className="studio-tool"
          title={t("toolBar.lock")}
          aria-label={t("toolBar.lock")}
          aria-pressed={appState.activeTool.locked}
          disabled={app.props.activeTool != null}
          onClick={onLockToggle}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="5" y="10" width="14" height="11" rx="3" />
            <path d="M8 10V6a4 4 0 0 1 8 0v4M12 14v3" />
          </svg>
        </button>
        <button
          type="button"
          className="studio-tool"
          title={t("toolBar.penMode")}
          aria-label={t("toolBar.penMode")}
          aria-pressed={appState.penMode}
          onClick={() => onPenModeToggle(null)}
        >
          {pencilIcon}
        </button>
      </div>
    </nav>
  );
};
